<div class="dsg-inspector">
  <!-- ── Nothing selected → artboard settings, so the pane is never empty ── -->
  <ng-container *ngIf="showCanvas()">
    <section class="dsg-section">
      <h4 class="dsg-section-title">Canvas</h4>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>W</span>
          <!-- Applied on Enter / blur: typing "2006" must not resize through 2, 20, 200. -->
          <input type="number" step="0.1" min="320" [ngModel]="canvas().width" [ngModelOptions]="{ updateOn: 'blur' }"
            (ngModelChange)="setCanvasSize('width', $event)" (keydown.enter)="$any($event.target).blur()" />
        </label>
        <label class="dsg-field">
          <span>H</span>
          <input type="number" step="0.1" min="200" [ngModel]="canvas().height" [ngModelOptions]="{ updateOn: 'blur' }"
            (ngModelChange)="setCanvasSize('height', $event)" (keydown.enter)="$any($event.target).blur()" />
        </label>
      </div>
      <label class="dsg-field dsg-field-wide">
        <span>Background</span>
        <input type="color" [ngModel]="canvas().background" (ngModelChange)="setCanvas({ background: $event })" />
      </label>
    </section>

    <section class="dsg-section">
      <h4 class="dsg-section-title">Grid</h4>
      <label class="dsg-field dsg-field-wide">
        <span>Size</span>
        <input type="number" min="1" [ngModel]="canvas().grid.size" (ngModelChange)="setGrid({ size: num($event) })" />
      </label>
      <label class="dsg-check">
        <input type="checkbox" [ngModel]="canvas().grid.visible" (ngModelChange)="setGrid({ visible: $event })" />
        <span>Show grid</span>
      </label>
      <label class="dsg-check">
        <input type="checkbox" [ngModel]="canvas().grid.snap" (ngModelChange)="setGrid({ snap: $event })" />
        <span>Snap to grid</span>
      </label>
    </section>

    <!-- Preview and the published dashboard always read the database when
         opened; this re-reads it while they stay open. -->
    <section class="dsg-section">
      <h4 class="dsg-section-title">Data refresh</h4>
      <label class="dsg-field dsg-field-wide">
        <span>Auto-refresh every (seconds)</span>
        <!-- Applied on Enter / blur, so typing "60" never runs a 6-second refresh. -->
        <input type="number" min="0" step="1" placeholder="Off" [ngModel]="canvas().autoRefreshSeconds ?? null"
          [ngModelOptions]="{ updateOn: 'blur' }" (ngModelChange)="setAutoRefresh($event)" (keydown.enter)="$any($event.target).blur()" />
      </label>
      <p class="dsg-inspector-hint">
        Preview and the published dashboard load fresh data whenever they open. Set seconds
        (minimum {{ minAutoRefreshSeconds }}) to also reload every table, chart and value while they stay open.
        Blank = off.
      </p>
    </section>

    <ng-container *ngTemplateOutlet="canvasFiltersSection"></ng-container>

    <p class="dsg-inspector-hint">Select a layer to edit its properties.</p>
  </ng-container>

  <!-- ── Multi-selection ────────────────────────────────────────────────── -->
  <p class="dsg-inspector-hint" *ngIf="!node() && selectionCount() > 1">
    {{ selectionCount() }} layers selected — align and distribute from the toolbar.
  </p>

  <!-- ── Single selection ───────────────────────────────────────────────── -->
  <ng-container *ngIf="node() as selected">
    <section class="dsg-section">
      <label class="dsg-field dsg-field-wide">
        <span>Name</span>
        <input type="text" [ngModel]="selected.name" (ngModelChange)="setName($event)" />
      </label>
    </section>

    <!-- A root FRAME publishes as one screen. Its box is the page's, so it is
         derived — the width from the viewport, the height from the content —
         and shown here rather than edited. -->
    <section class="dsg-section" *ngIf="isScreen()">
      <h4 class="dsg-section-title">Screen</h4>
      <!-- Applied on Enter / blur, so typing "2" on the way to "2000" never reflows the page. -->
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>W</span>
          <input type="number" min="320" [ngModel]="px(selected.width)" [ngModelOptions]="{ updateOn: 'blur' }"
            (ngModelChange)="setScreenSize('width', $event)" (keydown.enter)="$any($event.target).blur()" />
        </label>
        <label class="dsg-field">
          <span>H</span>
          <input type="number" min="200" [ngModel]="px(selected.height)" [ngModelOptions]="{ updateOn: 'blur' }"
            (ngModelChange)="setScreenSize('height', $event)" (keydown.enter)="$any($event.target).blur()" />
        </label>
      </div>
      <p class="dsg-inspector-hint">
        Width: {{ selected.screenSize?.width ? 'set by you' : 'automatic' }}
        <button *ngIf="selected.screenSize?.width" type="button" class="dsg-text-link" (click)="resetScreenSize('width')">Auto</button>
        · Height: {{ selected.screenSize?.height ? 'set by you' : 'automatic' }}
        <button *ngIf="selected.screenSize?.height" type="button" class="dsg-text-link" (click)="resetScreenSize('height')">Auto</button>
      </p>
      <p class="dsg-inspector-hint">
        Changing the width or height resizes the layout with it, so everything stays inside the page. Preview and the published page show this size. The dashed fold marks one screenful.
      </p>
    </section>

    <!-- A screen usually fills the canvas, so its filters are reachable from it too (the same canvas-level list). -->
    <ng-container *ngIf="isScreen()">
      <ng-container *ngTemplateOutlet="canvasFiltersSection"></ng-container>
    </ng-container>

    <!-- Position & size -->
    <section class="dsg-section" *ngIf="!isScreen()">
      <h4 class="dsg-section-title">Position &amp; size</h4>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>X</span>
          <input type="number" step="0.1" [ngModel]="px(selected.x)" (ngModelChange)="setGeometry('x', num($event))" />
        </label>
        <label class="dsg-field">
          <span>Y</span>
          <input type="number" step="0.1" [ngModel]="px(selected.y)" (ngModelChange)="setGeometry('y', num($event))" />
        </label>
      </div>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>W</span>
          <input type="number" step="0.1" min="1" [ngModel]="px(selected.width)" (ngModelChange)="setGeometry('width', num($event))" />
        </label>
        <label class="dsg-field">
          <span>H</span>
          <input type="number" step="0.1" min="1" [ngModel]="px(selected.height)" (ngModelChange)="setGeometry('height', num($event))" />
        </label>
      </div>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span matTooltip="Rotation">∠</span>
          <input type="number" [ngModel]="selected.rotation" (ngModelChange)="setRotation(num($event))" />
        </label>
        <label class="dsg-field">
          <span matTooltip="Opacity">%</span>
          <input type="number" min="0" max="100" [ngModel]="opacityPercent()" (ngModelChange)="setOpacity(num($event))" />
        </label>
      </div>
      <label class="dsg-field dsg-field-wide" *ngIf="selected.type === 'LINE'">
        <span>Orientation</span>
        <select [ngModel]="lineOrientation()" (ngModelChange)="setLineOrientation($event)">
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
          <option value="diagonal">Diagonal</option>
        </select>
      </label>
    </section>

    <!-- Text by level: one message per zone-slider level; the text shows the current level's. -->
    <section class="dsg-section" *ngIf="selected.type === 'TEXT' && levelSliderOptions().length">
      <h4 class="dsg-section-title">
        Text by level
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!selected.levelTexts" (ngModelChange)="toggleLevelTexts($event)" />
        </label>
      </h4>
      <p class="dsg-inspector-hint" *ngIf="!selected.levelTexts">
        Type a different message for each level of the zone slider - this text shows the one for the level the slider is in.
      </p>
      <ng-container *ngIf="selected.levelTexts">
        <!-- Pickers in this panel are ng-select, not native <select>: a native
             popup is sized to its longest option and spilled past the panel.
             The ng-select panel keeps the control's width and the hover
             tooltip (SsSelectTooltipDirective) shows a clipped label. -->
        <div class="dsg-field dsg-field-wide dsg-slide-field" *ngIf="levelSliderOptions().length > 1">
          <span>Zone slider</span>
          <ng-select
            #levelTextsSliderSel
            class="ss-select"
            [class.is-open]="!!levelTextsSliderSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="sliderChoices()"
            bindLabel="label"
            bindValue="value"
            [clearable]="false"
            [searchable]="false"
            [ngModel]="selected.levelTexts.sliderId || ''"
            (ngModelChange)="setLevelTextsSlider($event)"
          ></ng-select>
        </div>
        <p class="dsg-inspector-hint" *ngIf="!levelTextLevels().length">The zone slider has no levels yet - generate it first.</p>
        <label class="dsg-field dsg-field-wide dsg-level-text" *ngFor="let level of levelTextLevels(); trackBy: trackLevel"
          [class.is-current]="level === levelTextNow()?.label">
          <span>{{ level }}<em *ngIf="level === levelTextNow()?.label"> · now</em></span>
          <textarea rows="3"
            [value]="selected.levelTexts.texts[level] ?? ''"
            (change)="setLevelText(level, $any($event.target).value)"
            placeholder="Message at this level (blank = the text as typed)"></textarea>
        </label>
        <p class="dsg-inspector-hint">
          Use <code ngNonBindable>{{sliderValue}}</code> for the price and <code ngNonBindable>{{level}}</code> for the level name inside a message.
        </p>
      </ng-container>
    </section>

    <!-- Show at levels: visible only while a zone slider is in the ticked levels. -->
    <section class="dsg-section" *ngIf="!isScreen() && levelSliderOptions().length">
      <h4 class="dsg-section-title">
        Show at levels
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!selected.showAtLevels" (ngModelChange)="toggleShowAtLevels($event)" />
        </label>
      </h4>
      <p class="dsg-inspector-hint" *ngIf="!selected.showAtLevels">
        Show this only while the zone slider is in certain levels - e.g. a "Level 2 triggered" message.
        In any text, <code ngNonBindable>{{level}}</code> shows the slider's current level and
        <code ngNonBindable>{{sliderValue}}</code> its value; both follow the handle.
      </p>
      <ng-container *ngIf="selected.showAtLevels">
        <div class="dsg-field dsg-field-wide dsg-slide-field" *ngIf="levelSliderOptions().length > 1">
          <span>Zone slider</span>
          <ng-select
            #showAtSliderSel
            class="ss-select"
            [class.is-open]="!!showAtSliderSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="sliderChoices()"
            bindLabel="label"
            bindValue="value"
            [clearable]="false"
            [searchable]="false"
            [ngModel]="selected.showAtLevels.sliderId || ''"
            (ngModelChange)="setShowAtLevelsSlider($event)"
          ></ng-select>
        </div>
        <div class="dsg-field dsg-field-wide dsg-slide-field">
          <span>Show at</span>
          <ng-select
            #showAtLevelsSelect
            class="ss-select"
            [class.is-open]="!!showAtLevelsSelect.isOpen?.()"
            appendTo="body"
            [items]="levelChoices()"
            [multiple]="true"
            [closeOnSelect]="false"
            [clearable]="true"
            placeholder="Pick the levels"
            [ngModel]="selected.showAtLevels.levels"
            (ngModelChange)="setShowAtLevels($event)"
          ></ng-select>
        </div>
        <p class="dsg-inspector-hint">
          Now: <strong>{{ currentLevel()?.label || 'outside every level' }}</strong>.
          Hidden in Preview and on published screens at other levels; faded here so you can still edit it.
        </p>
      </ng-container>
    </section>

    <!-- Move with value: slide between marker siblings in Preview / published. -->
    <section class="dsg-section" *ngIf="!isScreen()">
      <h4 class="dsg-section-title">
        Move with value
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!selected.slide" (ngModelChange)="toggleSlide($event)" />
        </label>
      </h4>
      <ng-container *ngIf="selected.slide">
        <p class="dsg-inspector-hint">
          Moves between the stops you tick, by its own number
          (<strong>{{ slideValue() ?? 'no number found' }}</strong>).
          It moves on the canvas, in Preview and on published screens. While it is
          selected, the canvas shows where you drew it, so you can drag it.
        </p>
        <p class="dsg-inspector-hint" *ngIf="!slideCandidates().length">
          Put the markers in the same frame or group as this layer to use them as stops.
        </p>
        <ng-container *ngIf="slideCandidates().length">
          <div class="dsg-field dsg-field-wide dsg-slide-field">
            <span>Move between (stops)</span>
            <ng-select
              #slideStopsSelect
              class="ss-select"
              [class.is-open]="!!slideStopsSelect.isOpen?.()"
              appendTo="body"
              [items]="slideStopItems()"
              bindLabel="label"
              bindValue="id"
              [multiple]="true"
              [closeOnSelect]="false"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select the markers"
              [ngModel]="slideStopIds()"
              (ngModelChange)="setSlideStops($event)"
            ></ng-select>
          </div>
          <p class="dsg-inspector-hint is-error" *ngIf="slideUsableStops() < 2">
            Pick at least <strong>two</strong> stops with a number — it moves between them
            ({{ slideUsableStops() }} picked so far).
          </p>

          <div class="dsg-field dsg-field-wide dsg-slide-field">
            <span>If it lands on a stop</span>
            <ng-select
              #slideOverlapSel
              class="ss-select"
              [class.is-open]="!!slideOverlapSel.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="slideOverlapOptions"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="false"
              [ngModel]="selected.slide.avoidOverlap || 'nearest'"
              (ngModelChange)="setSlideOverlap($event)"
            ></ng-select>
          </div>

          <div class="dsg-field dsg-field-wide dsg-slide-field" *ngIf="slideFollowerItems().length">
            <span>Also move (travel with it)</span>
            <ng-select
              #slideFollowersSelect
              class="ss-select"
              [class.is-open]="!!slideFollowersSelect.isOpen?.()"
              appendTo="body"
              [items]="slideFollowerItems()"
              bindLabel="label"
              bindValue="id"
              [multiple]="true"
              [closeOnSelect]="false"
              [searchable]="true"
              [clearable]="true"
              placeholder="Its label, % text …"
              [ngModel]="slideFollowerIds()"
              (ngModelChange)="setSlideFollowers($event)"
            ></ng-select>
          </div>
        </ng-container>
      </ng-container>
    </section>

    <!-- Auto layout (frames) -->
    <section class="dsg-section" *ngIf="showLayout() && layout() as auto">
      <h4 class="dsg-section-title">Auto layout</h4>
      <label class="dsg-field dsg-field-wide">
        <span>Direction</span>
        <select [ngModel]="auto.mode" (ngModelChange)="setLayout({ mode: $event })">
          <option value="none">None (free)</option>
          <option value="row">Horizontal</option>
          <option value="column">Vertical</option>
        </select>
      </label>

      <ng-container *ngIf="auto.mode !== 'none'">
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Gap</span>
            <input type="number" min="0" [ngModel]="auto.gap" (ngModelChange)="setLayout({ gap: num($event) })" />
          </label>
          <label class="dsg-field">
            <span>Pad</span>
            <input type="number" min="0" [ngModel]="auto.padding[0]" (ngModelChange)="setLayoutPadding(num($event))" />
          </label>
        </div>
        <label class="dsg-field dsg-field-wide">
          <span>Align</span>
          <select [ngModel]="auto.align" (ngModelChange)="setLayout({ align: $event })">
            <option value="start">Start</option>
            <option value="center">Center</option>
            <option value="end">End</option>
            <option value="stretch">Stretch</option>
          </select>
        </label>
        <label class="dsg-field dsg-field-wide">
          <span>Justify</span>
          <select [ngModel]="auto.justify" (ngModelChange)="setLayout({ justify: $event })">
            <option value="start">Start</option>
            <option value="center">Center</option>
            <option value="end">End</option>
            <option value="between">Space between</option>
          </select>
        </label>
        <label class="dsg-check">
          <input type="checkbox" [ngModel]="auto.wrap" (ngModelChange)="setLayout({ wrap: $event })" />
          <span>Wrap</span>
        </label>
      </ng-container>
    </section>

    <!-- Appearance -->
    <section class="dsg-section" *ngIf="showFill()">
      <h4 class="dsg-section-title">Appearance</h4>
      <label class="dsg-field dsg-field-wide">
        <span>Fill</span>
        <select [ngModel]="fill().kind" (ngModelChange)="setFillKind($event)">
          <option value="none">None</option>
          <option value="solid">Solid</option>
        </select>
      </label>
      <label class="dsg-field dsg-field-wide" *ngIf="fill().kind === 'solid'">
        <span>Color</span>
        <input type="color" [ngModel]="fill().color || '#ffffff'" (ngModelChange)="setFillColor($event)" />
      </label>
      <label class="dsg-field dsg-field-wide" *ngIf="selected.type !== 'ELLIPSE'">
        <span>Radius</span>
        <input type="number" min="0" [ngModel]="radius()" (ngModelChange)="setRadius(num($event))" />
      </label>
      <label class="dsg-check" *ngIf="selected.type === 'FRAME'">
        <input
          type="checkbox"
          [ngModel]="selected.style.overflow === 'hidden'"
          (ngModelChange)="setOverflow($event)"
        />
        <span>Clip content</span>
      </label>
    </section>

    <!-- Stroke -->
    <section class="dsg-section">
      <h4 class="dsg-section-title">
        Stroke
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!stroke()" (ngModelChange)="toggleStroke($event)" />
        </label>
      </h4>
      <ng-container *ngIf="stroke() as line">
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Color</span>
            <input type="color" [ngModel]="line.color" (ngModelChange)="setStroke({ color: $event })" />
          </label>
          <!-- Figma's stroke "%": the colour's opacity, 0–100. -->
          <label class="dsg-field">
            <span matTooltip="Stroke colour opacity">%</span>
            <input type="number" min="0" max="100" [ngModel]="line.opacity ?? 100"
              (ngModelChange)="setStroke({ opacity: clampPercent($event) })" />
          </label>
        </div>
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>W</span>
            <input type="number" min="0" [ngModel]="line.width" (ngModelChange)="setStroke({ width: num($event) })" />
          </label>
          <label class="dsg-field">
            <span>Style</span>
            <select [ngModel]="line.style" (ngModelChange)="setStroke({ style: $event })">
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
          </label>
        </div>
      </ng-container>
    </section>

    <!-- Effects -->
    <section class="dsg-section">
      <h4 class="dsg-section-title">
        Shadow
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!shadow()" (ngModelChange)="toggleShadow($event)" />
        </label>
      </h4>
      <ng-container *ngIf="shadow() as drop">
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>X</span>
            <input type="number" [ngModel]="drop.x" (ngModelChange)="setShadow({ x: num($event) })" />
          </label>
          <label class="dsg-field">
            <span>Y</span>
            <input type="number" [ngModel]="drop.y" (ngModelChange)="setShadow({ y: num($event) })" />
          </label>
        </div>
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Blur</span>
            <input type="number" min="0" [ngModel]="drop.blur" (ngModelChange)="setShadow({ blur: num($event) })" />
          </label>
          <label class="dsg-field">
            <span>Spread</span>
            <input type="number" [ngModel]="drop.spread" (ngModelChange)="setShadow({ spread: num($event) })" />
          </label>
        </div>
      </ng-container>
      <label class="dsg-field dsg-field-wide">
        <span>Layer blur</span>
        <input type="number" min="0" [ngModel]="selected.style.blur || 0" (ngModelChange)="setBlur(num($event))" />
      </label>
    </section>

    <!-- Editing a button's popup screen: Done puts it away again. -->
    <section class="dsg-section" *ngIf="editingPopup() as editing">
      <h4 class="dsg-section-title">Popup screen</h4>
      <p class="dsg-inspector-hint">
        Editing “{{ editing.frame.name }}”. Resize the frame and everything inside rescales with it.
      </p>
      <button type="button" class="dsg-btn dsg-btn-primary dsg-btn-wide" (click)="finishPopupScreen()">
        <mat-icon>check</mat-icon> Done — hide popup screen
      </button>
    </section>

    <!-- Data: show a backend value inside the text. -->
    <section class="dsg-section" *ngIf="showTypography()">
      <h4 class="dsg-section-title">
        Data
        <button *ngIf="binding()" type="button" class="dsg-text-link" (click)="unbindText()">Unlink</button>
      </h4>

      <ng-container [ngSwitch]="dataService.datasets().status">
        <p class="dsg-inspector-hint" *ngSwitchCase="'loading'">Loading datasets…</p>
        <p class="dsg-inspector-hint is-error" *ngSwitchCase="'error'">
          {{ $any(dataService.datasets()).message }}
          <button type="button" class="dsg-text-link" (click)="dataService.loadDatasets(true)">Retry</button>
        </p>
      </ng-container>

      <div class="dsg-field dsg-field-wide">
        <span>Dataset</span>
        <ng-select
          #bindingDatasetSel
          class="ss-select"
          [class.is-open]="!!bindingDatasetSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="datasetChoices()"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="true"
          [ngModel]="binding()?.dataset ?? ''"
          (ngModelChange)="setBindingDataset($event)"
        ></ng-select>
      </div>

      <ng-container *ngIf="binding() as bound">
        <div class="dsg-field dsg-field-wide">
          <span>Column</span>
          <ng-select
            #bindingColumnSel
            class="ss-select"
            [class.is-open]="!!bindingColumnSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="columnChoices()"
            bindLabel="label"
            bindValue="value"
            [clearable]="false"
            [searchable]="true"
            [placeholder]="columnPlaceholder()"
            [ngModel]="bound.field || null"
            (ngModelChange)="patchBinding({ field: $event })"
          ></ng-select>
        </div>

        <div class="dsg-row dsg-row-2">
          <div class="dsg-field" *ngIf="bound.aggregation !== 'STATUS_COUNT'">
            <span>Summary</span>
            <ng-select
              #aggregationSel
              class="ss-select"
              [class.is-open]="!!aggregationSel.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="dataService.aggregations()"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="false"
              [ngModel]="bound.aggregation"
              (ngModelChange)="patchBinding({ aggregation: $event })"
            ></ng-select>
          </div>
          <label class="dsg-field">
            <span>Scale</span>
            <select [ngModel]="bound.scale ?? 'auto'" (ngModelChange)="patchBinding({ scale: $event })">
              <option *ngFor="let option of scaleOptions" [value]="option.value">{{ option.label }}</option>
            </select>
          </label>
        </div>

        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Prefix</span>
            <input type="text" placeholder="$" [ngModel]="bound.prefix ?? ''" (ngModelChange)="patchBinding({ prefix: $event || undefined })" />
          </label>
          <label class="dsg-field">
            <span>Suffix</span>
            <input type="text" placeholder="%" [ngModel]="bound.suffix ?? ''" (ngModelChange)="patchBinding({ suffix: $event || undefined })" />
          </label>
        </div>

        <label class="dsg-field dsg-field-wide">
          <span>Decimals</span>
          <input type="number" min="0" max="6" placeholder="auto"
            [ngModel]="bound.decimals ?? null"
            (ngModelChange)="patchBinding({ decimals: $event === null || $event === '' ? undefined : num($event) })" />
        </label>

        <label class="dsg-check" *ngIf="bound.field">
          <input type="checkbox" [ngModel]="bound.aggregation === 'STATUS_COUNT'"
            (ngModelChange)="patchBinding({ aggregation: $event ? 'STATUS_COUNT' : 'SUM' })" />
          <span>Count by this column's values (status counts)</span>
        </label>
        <!-- "Count per status" (STATUS_COUNTS): Column = group by; show one status's count. -->
        <ng-container *ngIf="bound.aggregation === 'STATUS_COUNT'">
          <div class="dsg-field dsg-field-wide">
            <span>Department</span>
            <ng-select
              #statusValueSel
              class="ss-select"
              [class.is-open]="!!statusValueSel.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="statusChoiceOptions()"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="true"
              [ngModel]="bound.statusValue ?? ''"
              (ngModelChange)="patchBinding({ statusValue: $event || undefined })"
            ></ng-select>
          </div>
          <label class="dsg-field dsg-field-wide">
            <span>Count column</span>
            <input type="text" placeholder="uuid" [ngModel]="bound.countField ?? ''" (ngModelChange)="patchBinding({ countField: $event || undefined })" />
          </label>
        </ng-container>

        <p class="dsg-inspector-hint" [class.is-error]="boundPreview().error">
          {{ boundPreview().label }}
        </p>
        <p class="dsg-inspector-hint">
          Put <code>{{ valueToken }}</code> in the text below where the number goes — e.g.
          <code>Total Sales: {{ valueToken }}</code>.
        </p>

        <!-- Which filters drive this text. Opt-in: a filter only changes the
             text when it lists the text as a target (here or in its own form). -->
        <div class="dsg-filtered-by" *ngIf="bound.field">
          <span class="dsg-filtered-by-label">Filtered by</span>
          <p class="dsg-inspector-hint" *ngIf="!textFilters().length">No filter targets this text — it always shows the full value.</p>
          <div class="dsg-filter-chips" *ngIf="textFilters().length">
            <span class="dsg-filter-chip" *ngFor="let filter of textFilters()">
              {{ filter.title }}
              <button type="button" (click)="unlinkTextFilter(filter.nodeId)" [attr.aria-label]="'Stop ' + filter.title + ' filtering this text'">×</button>
            </span>
          </div>
          <!-- An action picker: it links the chosen filter and snaps back to
               its placeholder (see onLinkFilterPick). -->
          <ng-select
            *ngIf="availableTextFilters().length"
            #linkFilterSel
            class="ss-select"
            [class.is-open]="!!linkFilterSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="availableTextFilters()"
            bindLabel="title"
            bindValue="nodeId"
            [clearable]="true"
            [searchable]="false"
            placeholder="+ Link a filter…"
            [ngModel]="null"
            (change)="onLinkFilterPick($event, linkFilterSel)"
          ></ng-select>
        </div>
      </ng-container>
    </section>

    <!-- Formula: a value worked out in the browser from inputs and other texts. -->
    <section class="dsg-section" *ngIf="showTypography()">
      <h4 class="dsg-section-title">
        Formula
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!formula()" (ngModelChange)="toggleFormula($event)" />
        </label>
      </h4>
      <ng-container *ngIf="formula() as calc">
        <label class="dsg-field dsg-field-wide">
          <span>Formula</span>
          <textarea rows="2" placeholder="[Current Volumes] * [Price]"
            [ngModel]="calc.expression" (ngModelChange)="patchFormula({ expression: $event })"></textarea>
        </label>
        <p class="dsg-inspector-hint" *ngIf="formulaSources().length">Click a layer to add it:</p>
        <div class="dsg-filter-chips" *ngIf="formulaSources().length">
          <button type="button" class="dsg-filter-chip" *ngFor="let source of formulaSources(); trackBy: trackFormulaSource"
            (click)="insertFormulaRef(source.name)">{{ source.name }}</button>
        </div>

        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Scale</span>
            <select [ngModel]="calc.scale ?? 'auto'" (ngModelChange)="patchFormula({ scale: $event })">
              <option *ngFor="let option of scaleOptions" [value]="option.value">{{ option.label }}</option>
            </select>
          </label>
          <label class="dsg-field">
            <span>Decimals</span>
            <input type="number" min="0" max="6" placeholder="auto"
              [ngModel]="calc.decimals ?? null"
              (ngModelChange)="patchFormula({ decimals: $event === null || $event === '' ? undefined : num($event) })" />
          </label>
        </div>
        <div class="dsg-row dsg-row-2">
          <label class="dsg-field">
            <span>Prefix</span>
            <input type="text" placeholder="$" [ngModel]="calc.prefix ?? ''" (ngModelChange)="patchFormula({ prefix: $event || undefined })" />
          </label>
          <label class="dsg-field">
            <span>Suffix</span>
            <input type="text" placeholder="%" [ngModel]="calc.suffix ?? ''" (ngModelChange)="patchFormula({ suffix: $event || undefined })" />
          </label>
        </div>

        <p class="dsg-inspector-hint" [class.is-error]="formulaPreview().error">{{ formulaPreview().label }}</p>
        <p class="dsg-inspector-hint">
          Use layer names in <code>[brackets]</code> with <code>+ - * / ( )</code>, and
          <code>first([A], [B])</code> (A if it has a value, else B), <code>if</code>,
          <code>min</code>, <code>max</code>, <code>round</code>, <code>abs</code>. Inputs update the result
          when you press Enter. Put <code>{{ valueToken }}</code> in the text where the result goes.
        </p>
      </ng-container>
    </section>

    <!-- Initials of: an avatar text shows the initials of another text's value
         (a user's name / email), following it as the value changes. -->
    <section class="dsg-section" *ngIf="showTypography() && selected.type === 'TEXT'">
      <h4 class="dsg-section-title">Initials of</h4>
      <div class="dsg-field dsg-field-wide">
        <ng-select
          #initialsSourceSel
          class="ss-select"
          [class.is-open]="!!initialsSourceSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="initialsSourceChoices()"
          bindLabel="label"
          bindValue="id"
          [clearable]="false"
          [searchable]="true"
          [ngModel]="selected.initialsOfId || ''"
          (ngModelChange)="setInitialsSource($event)"
        ></ng-select>
      </div>
      <p class="dsg-inspector-hint">
        Shows the initials of that text's value - "John Doe" → JD, talari.vs&#64;… → TV. What is typed here
        shows until that text has a value.
      </p>
    </section>

    <!-- Color by value: the text's colour follows its live number. -->
    <section class="dsg-section" *ngIf="showTypography()">
      <h4 class="dsg-section-title">
        Color by value
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="!!selected.valueColors?.length" (ngModelChange)="toggleValueColors($event)" />
        </label>
      </h4>
      <ng-container *ngIf="selected.valueColors?.length">
        <p class="dsg-inspector-hint">
          When the value arrives, the text takes the colour of the first range it falls in
          (From and To included; leave a box empty for no limit). A limit can be a <strong>marker</strong>
          on the canvas - e.g. MFN, FLOOR - so the bands follow each record's own prices. Value now:
          <strong>{{ valueColorPreview() ?? 'no number yet' }}</strong>
        </p>
        <div class="dsg-field dsg-field-wide">
          <span>Colour</span>
          <ng-select
            #valueColorTargetSel
            class="ss-select"
            [class.is-open]="!!valueColorTargetSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="valueColorTargetOptions"
            bindLabel="label"
            bindValue="value"
            [clearable]="false"
            [searchable]="false"
            [ngModel]="selected.valueColorTarget || 'text'"
            (ngModelChange)="setValueColorTarget($event)"
          ></ng-select>
        </div>
        <div class="dsg-field dsg-field-wide">
          <span>Compare</span>
          <ng-select
            #valueColorSourceSel
            class="ss-select"
            [class.is-open]="!!valueColorSourceSel.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="valueColorSourceChoices()"
            bindLabel="label"
            bindValue="id"
            [clearable]="false"
            [searchable]="false"
            [ngModel]="selected.valueColorSourceId || ''"
            (ngModelChange)="setValueColorSource($event)"
          ></ng-select>
        </div>
        <!-- One card per range: each limit is a Number, a marker on the canvas,
             or a dataset Column (read for the current filters). -->
        <div class="dsg-value-color-range" *ngFor="let range of selected.valueColors; let i = index; trackBy: trackByIndex">
          <div class="dsg-value-color-range-head">
            <span>Range {{ i + 1 }}</span>
            <input type="color" [ngModel]="range.color" (ngModelChange)="patchValueColor(i, { color: $event })" [attr.aria-label]="'Colour of range ' + (i + 1)" />
            <button type="button" class="dsg-value-color-remove" (click)="removeValueColor(i)" aria-label="Remove range">×</button>
          </div>
          <div class="dsg-value-color-side">
            <span class="dsg-value-color-side-label">From</span>
            <ng-select
              #fromMarkerSel
              class="ss-select"
              [class.is-open]="!!fromMarkerSel.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="valueColorLimitChoices()"
              bindLabel="label"
              bindValue="id"
              [clearable]="false"
              [searchable]="true"
              ariaLabel="From: a number, a marker or a column"
              [ngModel]="range.fromId || ''"
              (ngModelChange)="patchValueColor(i, { fromId: $event || undefined })"
            ></ng-select>
            <input *ngIf="!range.fromId" type="number" placeholder="no min" [ngModel]="range.from ?? null" (ngModelChange)="patchValueColor(i, { from: rangeNumber($event) })" />
          </div>
          <div class="dsg-value-color-side">
            <span class="dsg-value-color-side-label">To</span>
            <ng-select
              #toMarkerSel
              class="ss-select"
              [class.is-open]="!!toMarkerSel.isOpen()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="valueColorLimitChoices()"
              bindLabel="label"
              bindValue="id"
              [clearable]="false"
              [searchable]="true"
              ariaLabel="To: a number, a marker or a column"
              [ngModel]="range.toId || ''"
              (ngModelChange)="patchValueColor(i, { toId: $event || undefined })"
            ></ng-select>
            <input *ngIf="!range.toId" type="number" placeholder="no max" [ngModel]="range.to ?? null" (ngModelChange)="patchValueColor(i, { to: rangeNumber($event) })" />
          </div>
        </div>
        <p class="dsg-inspector-hint" *ngIf="!valueColorColumnChoices().length">
          Columns appear in the From / To lists once a text on this screen is bound to a dataset.
        </p>
        <button type="button" class="dsg-value-color-add" (click)="addValueColor()">+ Add range</button>
      </ng-container>
    </section>

    <!-- Typography -->
    <section class="dsg-section" *ngIf="showTypography() && text() as type">
      <h4 class="dsg-section-title">Typography</h4>
      <label class="dsg-field dsg-field-wide">
        <span>Text</span>
        <textarea rows="2" [ngModel]="type.value" (ngModelChange)="setText({ value: $event })"></textarea>
      </label>
      <label class="dsg-field dsg-field-wide">
        <span>Font</span>
        <select
          [ngModel]="type.fontFamily"
          (ngModelChange)="setText({ fontFamily: $event })"
        >
          <!-- A font already on the node that is not in the list (an older
               design, or one typed in before this was a dropdown) still shows,
               so switching to a select never silently drops it. -->
          <option *ngIf="!isKnownFont(type.fontFamily)" [ngValue]="type.fontFamily">
            {{ type.fontFamily || 'Default' }}
          </option>
          <option *ngFor="let font of fontFamilies()" [ngValue]="font.value">
            {{ font.label }}
          </option>
        </select>
      </label>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>Size</span>
          <input type="number" min="1" [ngModel]="type.fontSize" (ngModelChange)="setText({ fontSize: num($event) })" />
        </label>
        <label class="dsg-field">
          <span>Weight</span>
          <select [ngModel]="type.fontWeight" (ngModelChange)="setText({ fontWeight: num($event) })">
            <option *ngFor="let weight of fontWeights" [value]="weight">{{ weight }}</option>
          </select>
        </label>
      </div>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>Line</span>
          <input type="number" step="0.1" min="0.5" [ngModel]="type.lineHeight" (ngModelChange)="setText({ lineHeight: num($event) })" />
        </label>
        <label class="dsg-field">
          <span>Spacing</span>
          <input type="number" step="0.1" [ngModel]="type.letterSpacing" (ngModelChange)="setText({ letterSpacing: num($event) })" />
        </label>
      </div>
      <!-- Bold/italic/underline/strikethrough. Bold is the weight the rest of
           the panel already edits, so toggling it moves between 400 and 700
           rather than introducing a second source of truth for boldness. -->
      <div class="dsg-row dsg-text-style-row">
        <button
          type="button" class="dsg-style-toggle" [class.is-on]="type.fontWeight >= 600"
          matTooltip="Bold"
          (click)="setText({ fontWeight: type.fontWeight >= 600 ? 400 : 700 })"
        ><b>B</b></button>
        <button
          type="button" class="dsg-style-toggle" [class.is-on]="type.fontStyle === 'italic'"
          matTooltip="Italic"
          (click)="setText({ fontStyle: type.fontStyle === 'italic' ? 'normal' : 'italic' })"
        ><i>I</i></button>
        <button
          type="button" class="dsg-style-toggle" [class.is-on]="type.textDecoration === 'underline'"
          matTooltip="Underline"
          (click)="setText({ textDecoration: type.textDecoration === 'underline' ? 'none' : 'underline' })"
        ><u>U</u></button>
        <button
          type="button" class="dsg-style-toggle" [class.is-on]="type.textDecoration === 'line-through'"
          matTooltip="Strikethrough"
          (click)="setText({ textDecoration: type.textDecoration === 'line-through' ? 'none' : 'line-through' })"
        ><s>S</s></button>
      </div>
      <div class="dsg-row dsg-row-2">
        <label class="dsg-field">
          <span>Case</span>
          <select [ngModel]="type.textTransform || 'none'" (ngModelChange)="setText({ textTransform: $event })">
            <option value="none">As typed</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="lowercase">lowercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </label>
        <label class="dsg-field">
          <span matTooltip="Vertical alignment">V-align</span>
          <select [ngModel]="type.verticalAlign || 'top'" (ngModelChange)="setText({ verticalAlign: $event })">
            <option value="top">Top</option>
            <option value="middle">Middle</option>
            <option value="bottom">Bottom</option>
          </select>
        </label>
      </div>
      <label class="dsg-field dsg-field-wide">
        <span>Color</span>
        <input type="color" [ngModel]="type.color" (ngModelChange)="setText({ color: $event })" />
      </label>
      <div class="dsg-segmented">
        <button
          *ngFor="let align of alignments"
          type="button"
          [class.is-active]="type.align === align"
          (click)="setText({ align: align })"
          [matTooltip]="'Align ' + align"
        >
          <mat-icon>format_align_{{ align }}</mat-icon>
        </button>
      </div>
    </section>

    <!-- Icon -->
    <section class="dsg-section" *ngIf="showIcon() && icon() as glyph">
      <div class="dsg-field dsg-field-wide">
        <span>Action</span>
        <ng-select
          #iconActionSel
          class="ss-select"
          [class.is-open]="!!iconActionSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="iconActionChoices"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="glyph.action || 'none'"
          (ngModelChange)="setIcon({ action: $event })"
        ></ng-select>
      </div>
      <small>Actions run in Preview and published dashboards.</small>
      <ng-container *ngIf="glyph.action === 'info'">
        <label class="dsg-field dsg-field-wide"><span>Information source</span>
          <select [ngModel]="glyph.info?.source || 'static'" (ngModelChange)="setIconInfo({ source: $event })">
            <option value="static">Static text</option><option value="api">API (GET)</option>
          </select>
        </label>
        <label class="dsg-field dsg-field-wide"><span>Title</span>
          <input type="text" [ngModel]="glyph.info?.title || ''" (ngModelChange)="setIconInfo({ title: $event })" />
        </label>
        <label class="dsg-field dsg-field-wide" *ngIf="glyph.info?.source !== 'api'"><span>Information text</span>
          <textarea rows="5" [ngModel]="glyph.info?.text || ''" (ngModelChange)="setIconInfo({ text: $event })"></textarea>
        </label>
        <ng-container *ngIf="glyph.info?.source === 'api'">
          <label class="dsg-field dsg-field-wide"><span>API URL</span>
            <input type="text" placeholder="/dashboard/info" [ngModel]="glyph.info?.url || ''" (ngModelChange)="setIconInfo({ url: $event })" />
          </label>
          <label class="dsg-field dsg-field-wide"><span>Response text path (optional)</span>
            <input type="text" placeholder="data.description" [ngModel]="glyph.info?.responsePath || ''" (ngModelChange)="setIconInfo({ responsePath: $event })" />
          </label>
          <small>GET returns text or JSON. Use a response path for nested JSON text.</small>
        </ng-container>
      </ng-container>
      <div class="dsg-field dsg-field-wide" *ngIf="glyph.action === 'upload'">
        <span>Upload control</span>
        <ng-select
          #uploadTargetSel
          class="ss-select"
          [class.is-open]="!!uploadTargetSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="uploadTargetChoices()"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="glyph.targetNodeId || ''"
          (ngModelChange)="setIcon({ targetNodeId: $event })"
        ></ng-select>
      </div>
      <h4 class="dsg-section-title">Icon</h4>

      <div class="dsg-icon-preview">
        <lucide-angular
          [img]="$any(iconData())"
          [size]="36"
          [color]="glyph.color"
          [strokeWidth]="glyph.strokeWidth"
          [absoluteStrokeWidth]="!!glyph.absoluteStrokeWidth"
        ></lucide-angular>
        <div class="dsg-icon-preview-meta">
          <strong>{{ glyph.name }}</strong>
          <small>Pick another from the Icons panel</small>
        </div>
      </div>

      <label class="dsg-field dsg-field-wide">
        <span>Colour</span>
        <input type="color" [ngModel]="glyph.color" (ngModelChange)="setIcon({ color: $event })" />
      </label>

      <label class="dsg-field dsg-field-wide">
        <span>Size</span>
        <input type="number" min="8" [ngModel]="iconSize()" (ngModelChange)="setIconSize(num($event))" />
      </label>

      <!-- Stroke width is the control a stroke-first icon set exists for. -->
      <label class="dsg-field dsg-field-wide">
        <span>Stroke</span>
        <input
          type="range"
          min="0.5"
          max="4"
          step="0.25"
          [ngModel]="glyph.strokeWidth"
          (ngModelChange)="setIcon({ strokeWidth: num($event) })"
        />
        <em class="dsg-field-value">{{ glyph.strokeWidth }}</em>
      </label>

      <label class="dsg-check">
        <input
          type="checkbox"
          [ngModel]="glyph.absoluteStrokeWidth"
          (ngModelChange)="setIcon({ absoluteStrokeWidth: $event })"
        />
        <span matTooltip="Keep the stroke the same thickness as the icon scales">Constant stroke</span>
      </label>

      <label class="dsg-check">
        <input type="checkbox" [ngModel]="iconFilled()" (ngModelChange)="toggleIconFill($event)" />
        <span>Fill</span>
      </label>

      <label class="dsg-field dsg-field-wide" *ngIf="iconFilled()">
        <span>Fill colour</span>
        <input type="color" [ngModel]="glyph.fill" (ngModelChange)="setIcon({ fill: $event })" />
      </label>
    </section>

    <!-- Image -->
    <section class="dsg-section" *ngIf="showImage()">
      <h4 class="dsg-section-title">Image</h4>
      <label class="dsg-file">
        <input type="file" accept="image/*" (change)="onImageFile($event)" hidden />
        <span>Choose image…</span>
      </label>
      <label class="dsg-field dsg-field-wide">
        <span>Fit</span>
        <select [ngModel]="selected.image?.fit" (ngModelChange)="setImage({ fit: $event })">
          <option value="cover">Cover</option>
          <option value="contain">Contain</option>
          <option value="fill">Fill</option>
          <option value="none">None</option>
        </select>
      </label>
      <label class="dsg-field dsg-field-wide">
        <span>Alt</span>
        <input type="text" [ngModel]="selected.image?.alt" (ngModelChange)="setImage({ alt: $event })" />
      </label>
    </section>

    <!-- Family-specific design settings — an input control's label and range,
         editable now even though its data comes later. -->
    <section class="dsg-section" *ngIf="showWidget() && specControls().length">
      <h4 class="dsg-section-title">Control</h4>
      <app-design-control-renderer
        *ngFor="let control of specControls(); trackBy: trackByControlKey"
        [control]="control"
        [values]="appearance()"
        [options]="configOptions()"
        (valueChange)="onControlChange($event)"
      ></app-design-control-renderer>
    </section>

    <!-- Zone slider: value / range / zones / badge / style, all design tier.
         The dataset-driven parts live in the Data tab. -->
    <!-- Value formula: the input / slider value is calculated from other layers. -->
    <section class="dsg-section" *ngIf="isInputValueWidget()">
      <h4 class="dsg-section-title">
        Value formula
        <label class="dsg-section-toggle">
          <input type="checkbox" [ngModel]="valueFormula() !== null" (ngModelChange)="toggleValueFormula($event)" />
        </label>
      </h4>
      <ng-container *ngIf="valueFormula() !== null">
        <label class="dsg-field dsg-field-wide">
          <span>Formula</span>
          <textarea rows="2" placeholder="first([Future Volumes], [Current Volumes])"
            [ngModel]="valueFormula()" (ngModelChange)="setValueFormula($event)"></textarea>
        </label>
        <p class="dsg-inspector-hint" *ngIf="valueFormulaSources().length">Click a layer to add it:</p>
        <div class="dsg-filter-chips" *ngIf="valueFormulaSources().length">
          <button type="button" class="dsg-filter-chip" *ngFor="let source of valueFormulaSources(); trackBy: trackFormulaSource"
            (click)="insertValueFormulaRef(source.name)">{{ source.name }}</button>
        </div>
        <p class="dsg-inspector-hint" [class.is-error]="valueFormulaPreview().error">{{ valueFormulaPreview().label }}</p>
        <p class="dsg-inspector-hint">
          <code>first([A], [B])</code> uses A when it has a value, otherwise B.
          Also: <code>+ - * / ( )</code>, <code>if(cond, a, b)</code>, <code>&gt; &lt; ==</code>,
          <code>isempty</code>, <code>min</code>, <code>max</code>, <code>round</code>.
          Recalculates when an input it uses changes (Enter). Dragging the slider by hand
          holds until one of those inputs changes.
        </p>
      </ng-container>
    </section>

    <section class="dsg-section" *ngIf="showWidget() && isZoneSlider()">
      <h4 class="dsg-section-title">Zone slider</h4>
      <app-design-zone-slider-settings
        [appearance]="appearance()"
        [options]="configOptions()"
        [zonesFromData]="zoneSliderZonesFromData()"
        (appearanceChange)="setAppearance($event)"
      ></app-design-zone-slider-settings>
    </section>

    <!-- Table & Matrix (Normal / Editable / Drill-Down): per-column generic
         cell configuration — colours, conditional rules, badges, bars, cell
         inputs / dropdowns — through the ONE shared editor. Same section the
         nested table has below; writes appearance.columnCellConfigs. -->
    <section class="dsg-section" *ngIf="showWidget() && isTableMatrix()">
      <h4 class="dsg-section-title">Columns</h4>
      <app-design-table-matrix-columns [contextKey]="node()?.id || ''"
        [data]="tableMatrixData()"
        [configs]="tableColumnCellConfigs()"
        [buttonContext]="cellButtonContext()"
        [tableName]="node()?.name || ''"
        (configsChange)="onTableColumnCellConfigsChange($event)"
      ></app-design-table-matrix-columns>
    </section>

    <section class="dsg-section" *ngIf="showWidget() && isFileUpload()">
      <h4 class="dsg-section-title">File upload</h4>
      <button type="button" class="dsg-upload-order" (click)="reorderUpload('front')">Bring upload to front</button>
      <button type="button" class="dsg-upload-order" (click)="reorderUpload('back')">Send upload to back</button>
      <app-design-file-upload-settings [appearance]="appearance()" (appearanceChange)="setAppearance($event)" [value]="fileUploadConfig()" (valueChange)="onFileUploadConfigChange($event)"></app-design-file-upload-settings>
    </section>

    <!-- Button: one feature (download / API / filter / navigate / popup) and its settings. -->
    <section class="dsg-section" *ngIf="showWidget() && isButton()">
      <h4 class="dsg-section-title">Button</h4>
      <app-design-button-settings
        [appearance]="appearance()"
        [options]="configOptions()"
        [targetOptions]="buttonTargetOptions()"
        [widgetOptions]="buttonWidgetOptions()"
        [sliderOptions]="buttonSliderOptions()"
        [approvalTableOptions]="buttonApprovalTableOptions()"
        [widgetDatasets]="buttonWidgetDatasets()"
        [valueSourceOptions]="buttonValueSourceOptions()"
        [popupOptions]="buttonPopupOptions()"
        [screenOptions]="buttonScreenOptions()"
        [dashboardOptions]="buttonDashboardOptions()"
        (appearanceChange)="setAppearance($event)"
        [popupScreenState]="buttonPopupState()"
        (createPopupScreen)="createPopupScreen()"
        (openPopupScreen)="openPopupScreen()"
        (hidePopupScreen)="finishPopupScreen()"
      ></app-design-button-settings>
    </section>

    <section class="dsg-section" *ngIf="showWidget() && isNestedTable()">
      <h4 class="dsg-section-title">Columns</h4>
      <app-design-nested-table-columns [contextKey]="node()?.id || ''"
        [data]="nestedTableData()"
        [overrides]="nestedTableOverrides()"
        [buttonContext]="cellButtonContext()"
        [tableName]="node()?.name || ''"
        (overridesChange)="onNestedOverridesChange($event)"
      ></app-design-nested-table-columns>
    </section>

    <!-- ── Design tier: how the chart LOOKS. No dataset needed, so it comes
         FIRST — you pick stacked/horizontal while designing and the canvas
         shows it immediately. ────────────────────────────────────────────── -->
    <section class="dsg-section" *ngIf="showWidget() && variantOptions().length">
      <h4 class="dsg-section-title">Chart layout</h4>

      <label class="dsg-field dsg-field-wide">
        <span>{{ variantLabel() }}</span>
        <select [ngModel]="currentVariant()" (ngModelChange)="setAppearance({ variant: $event })">
          <option *ngFor="let variant of variantOptions()" [ngValue]="variant.value">
            {{ variant.label }}
          </option>
        </select>
      </label>

      <label class="dsg-field dsg-field-wide" *ngIf="showOrientation()">
        <span>Orientation</span>
        <select
          [ngModel]="currentOrientation()"
          (ngModelChange)="setAppearance({ orientation: $event })"
        >
          <option *ngFor="let option of orientationOptions" [ngValue]="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>

      <p class="dsg-inspector-hint" *ngIf="variantHint()">{{ variantHint() }}</p>
    </section>

    <!-- Format controls, straight from the builder config. Collapsed by default
         so ~60 controls do not bury the rest of the pane. -->
    <section class="dsg-section dsg-format" *ngIf="showWidget() && formatGroups().length">
      <h4 class="dsg-section-title">Format</h4>

      <div class="dsg-format-group" *ngFor="let group of formatGroups(); trackBy: trackByGroupName">
        <button
          type="button"
          class="dsg-format-group-head"
          [class.is-open]="isGroupOpen(group.name)"
          (click)="toggleGroup(group.name)"
        >
          <mat-icon>{{ isGroupOpen(group.name) ? 'expand_more' : 'chevron_right' }}</mat-icon>
          <span>{{ group.name }}</span>
          <small>{{ group.controls.length }}</small>
        </button>

        <div class="dsg-format-group-body" *ngIf="isGroupOpen(group.name)">
          <app-design-control-renderer
            *ngFor="let control of group.controls; trackBy: trackByControlKey"
            [control]="control"
            [values]="appearance()"
            [options]="configOptions()"
            (valueChange)="onControlChange($event)"
          ></app-design-control-renderer>
        </div>
      </div>
    </section>

    <!-- Data binding (dataset / field / aggregation) is the DATA TIER and is
         deliberately NOT here — the design pass comes first. See the two-tier
         split in docs/DESIGN_BUILDER.md. -->
    <section class="dsg-section" *ngIf="showWidget() && selected.widget as widget">
      <h4 class="dsg-section-title">{{ widgetLabel() }}</h4>
      <p class="dsg-inspector-hint" *ngIf="isSample()">
        Showing <strong>sample data</strong> so the component is visible while you
        design. Data mapping comes later — it is stripped on publish.
      </p>
    </section>
  </ng-container>
</div>

<!-- Canvas-level filters: each filters only the components / texts it targets.
     Shown with nothing selected and with a screen selected. -->
<ng-template #canvasFiltersSection>
  <section class="dsg-section">
    <h4 class="dsg-section-title">Canvas filters</h4>
    <app-canvas-filters></app-canvas-filters>
  </section>
</ng-template>
